// client/src/components/common/AppHeader.jsx
//
// Light top-navigation header:
//   [ Bozarth badge + "BOZARTH / Connect" wordmark ]  [ search ]  [ avatar | ← Dashboard ]
//
// The badge + wordmark link to the dashboard route. The navy circular avatar
// shows the logged-in agent's initials and opens a menu with Reports,
// Subscriptions, and Logout. The "← Dashboard" outlined button routes to the
// dashboard.
//
// This is a standalone header (does NOT replace common/Header.jsx). Layout,
// colors and radii come from the shared token file (../../theme/tokens).

import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, LogOut, FileBarChart, ShieldCheck, Bell, Download } from 'lucide-react';
import { colors, radii } from '../../../theme/tokens';
import { useCallContext } from '../../../contexts/CallContext';
import { apiClient } from '../../../services/apiClient';
import HeaderSearchBox from '../V4Header/AppBarSearchV4';
import SubscriptionStatusModal from '../SubscriptionStatusModal';
import { useAppDownload, formatSize } from '../../../hooks/useAppDownload';

/**
 * Resolve the logged-in agent's human-readable name.
 * The real name lives in currentUser.roleDetails (firstName / lastName), same
 * as everywhere else the agent is named; top-level name fields are honoured if
 * present. The 3CX username (the extension) is a last resort, not a name — the
 * avatar should show the agent's initials, not the extension digits.
 * Returns '' when no usable name (not the extension) is available.
 */
export const getDisplayName = (currentUser) => {
  if (!currentUser) return '';
  const rd = currentUser.roleDetails || {};
  const fromRole = [rd.firstName, rd.lastName].filter(Boolean).join(' ').trim();
  return (
    currentUser.name ||
    currentUser.displayName ||
    currentUser.fullName ||
    fromRole ||
    rd.fullName ||
    rd.name ||
    ''
  ).trim();
};

/**
 * Derive up-to-two-letter initials from the logged-in user's name.
 * Falls back to the 3CX username (extension) only when no name is available.
 * Returns "?" when nothing usable is present.
 */
export const getInitials = (currentUser) => {
  if (!currentUser) return '?';

  const source = getDisplayName(currentUser) || currentUser.username || '';

  const cleaned = String(source).trim();
  if (!cleaned) return '?';

  const parts = cleaned.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return cleaned.slice(0, 2).toUpperCase();
};

// Maps the connection status bucket (from getStatusColor) to a ring color for
// the avatar border.
const STATUS_RING = {
  connected: '#22c55e',    // green
  reconnecting: '#f59e0b', // amber
  offline: '#ef4444',      // red
  disconnected: '#ef4444', // red
};

const AppHeader = ({
  currentUser,
  handleLogout,
  onSearch,
  isSearching,
  searchEnabled = true,
  dashboardTo = '/dashboard',
  getStatusColor,
}) => {
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isGeneratingToken, setIsGeneratingToken] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [isBellMenuOpen, setIsBellMenuOpen] = useState(false);
  const profileMenuRef = useRef(null);
  const avatarBtnRef = useRef(null);
  const menuRef = useRef(null);
  const bellMenuRef = useRef(null);

  // Desktop-app download: version check, "New Update" dot, and download action.
  const { info: appInfo, hasUpdate, isDownloading, download } = useAppDownload();
  const navigate = useNavigate();
  const location = useLocation();
  const { showCustomerPopup, closeCustomerPopup } = useCallContext();

  // The call / customer-detail screen renders on the dashboard route itself
  // (via showCustomerPopup), so it isn't a distinct URL. Show the
  // "← Dashboard" back button whenever that screen is open OR we're off the
  // dashboard route; hide it only on the bare dashboard list, where there's
  // nothing to go back to.
  // Both the index route ('/') and dashboardTo render the same dashboard list.
  const isOnDashboardRoute =
    location.pathname === dashboardTo || location.pathname === '/';
  const showDashboardButton = showCustomerPopup || !isOnDashboardRoute;

  // Returning to the dashboard means closing the call screen (its own state,
  // not a route) and landing on the dashboard list.
  const handleBackToDashboard = () => {
    if (showCustomerPopup) closeCustomerPopup();
    navigate(dashboardTo);
  };

  const initials = getInitials(currentUser);
  const statusRingColor = STATUS_RING[getStatusColor?.()] || STATUS_RING.disconnected;
  const displayName = getDisplayName(currentUser) || currentUser?.username || 'Account';

  // Close the profile menu on outside click or Escape (listeners attached only
  // while it is open). Escape returns focus to the avatar so keyboard users
  // aren't dropped at the top of the document.
  useEffect(() => {
    if (!isProfileMenuOpen) return;
    const handleClickOutside = (event) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setIsProfileMenuOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsProfileMenuOpen(false);
        avatarBtnRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isProfileMenuOpen]);

  // Close the bell dropdown on outside click or Escape.
  useEffect(() => {
    if (!isBellMenuOpen) return;
    const handleClickOutside = (event) => {
      if (bellMenuRef.current && !bellMenuRef.current.contains(event.target)) {
        setIsBellMenuOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsBellMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isBellMenuOpen]);

  // Download from either entry point (bell dropdown or profile menu), then close
  // the menus. The hook clears the update dot once the download starts.
  const handleDownloadClick = () => {
    setIsBellMenuOpen(false);
    setIsProfileMenuOpen(false);
    download();
  };

  // Enabled menu items, in DOM order — drives arrow-key navigation.
  const menuItems = () =>
    Array.from(menuRef.current?.querySelectorAll('[role="menuitem"]') ?? []).filter(
      (item) => !item.disabled
    );

  // ARIA menu pattern: role="menu" tells screen readers arrow keys work, so
  // they must — focus lands on the first item when the menu opens, and
  // ArrowDown/ArrowUp step through the items (wrapping), Home/End jump.
  useEffect(() => {
    if (isProfileMenuOpen) menuItems()[0]?.focus();
  }, [isProfileMenuOpen]);

  const handleMenuKeyDown = (event) => {
    const items = menuItems();
    if (!items.length) return;
    const index = items.indexOf(document.activeElement);
    let next = null;
    if (event.key === 'ArrowDown') next = items[(index + 1) % items.length];
    else if (event.key === 'ArrowUp') next = items[(index - 1 + items.length) % items.length];
    else if (event.key === 'Home') next = items[0];
    else if (event.key === 'End') next = items[items.length - 1];
    if (next) {
      event.preventDefault();
      next.focus();
    }
  };

  const handleLogoutClick = () => {
    setIsProfileMenuOpen(false);
    avatarBtnRef.current?.focus();
    if (handleLogout) handleLogout();
  };

  // Open Subscriptions modal from the dropdown.
  const handleSubscriptionsClick = () => {
    setIsProfileMenuOpen(false);
    avatarBtnRef.current?.focus();
    setIsSubscriptionModalOpen(true);
  };

  // Request a report handshake token, then open the reports module in a new tab.
  const handleReportsClick = async () => {
    if (isGeneratingToken) return;

    try {
      setIsGeneratingToken(true);
      const response = await apiClient.post('/handoff/report-token');

      if (response.data.success && response.data.token) {
        const { token, user, email } = response.data;
        const reportsUrl = import.meta.env.VITE_BOZARTH_REPORTS_URL;
        const finalUrl = `${reportsUrl}?token=${token}&user=${user}&email=${encodeURIComponent(email)}`;
        window.open(finalUrl, '_blank');
        setIsProfileMenuOpen(false);
        avatarBtnRef.current?.focus();
      } else {
        console.error('❌ Failed to get handshake token:', response.data.message);
        alert('Failed to generate secure reports session. Please try again.');
      }
    } catch (error) {
      console.error('❌ Error during report handshake:', error);
      alert('An error occurred while connecting to the reports module.');
    } finally {
      setIsGeneratingToken(false);
    }
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 w-full bg-white border-b border-gray-200"
      style={{ borderColor: colors.gray200 }}
    >
      {/* 64px tall (matches AppLayout's pt-16 offset), vertically centered; the gap halves below `lg` so the in-flow search field keeps its width (T7.3). */}
      <div className="relative flex items-center gap-3 lg:gap-6 px-4 sm:px-6 h-16">
        {/* Left — badge logo + two-line wordmark, links to dashboard */}
        <Link
          to={dashboardTo}
          onClick={() => { if (showCustomerPopup) closeCustomerPopup(); }}
          className="flex items-center gap-3 flex-shrink-0 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
          style={{ '--tw-ring-color': colors.navy }}
          aria-label="Go to dashboard"
          title="Bozarth Connect — go to dashboard"
        >
          <img
            src="/logo/bozarthconnectlogo.svg"
            alt="Bozarth Connect"
            className="h-10 w-10 object-contain flex-shrink-0"
          />
          {/* Wordmark drops below `lg`, badge alone identifies the app (T7.3); the link's aria-label + title keep the name announced and on hover. */}
          <span className="hidden lg:flex flex-col justify-center h-10 leading-tight">
            <span
              className="block text-lg font-extrabold tracking-wide leading-none"
              style={{ color: colors.navy }}
            >
              BOZARTH
            </span>
            <span
              className="block text-xs font-semibold tracking-wide leading-none mt-0.5"
              style={{ color: colors.navy }}
            >
              Connect
            </span>
          </span>
        </Link>

        {/* Center — search box: absolutely centered from `lg` up, capped at 448px below `wide` (T7.2); in the flex flow below `lg`, where centering painted it over the logo and avatar (T7.3). */}
        {searchEnabled && (
          <div className="flex items-center flex-1 min-w-0 lg:flex-none lg:absolute lg:inset-y-0 lg:left-1/2 lg:-translate-x-1/2 lg:w-full lg:max-w-md wide:max-w-xl lg:px-4 pointer-events-none">
            <div className="w-full pointer-events-auto">
              <HeaderSearchBox onSearch={onSearch} isSearching={isSearching} />
            </div>
          </div>
        )}

        {/* Right — bell | avatar menu | divider | Dashboard button */}
        <div className="flex items-center gap-3 lg:gap-6 flex-shrink-0 ml-auto">
          {/* Notification bell → "New Update" dot + quick download */}
          <div className="relative" ref={bellMenuRef}>
            <button
              type="button"
              onClick={() => setIsBellMenuOpen((open) => !open)}
              className="relative flex items-center justify-center h-10 w-10 rounded-full text-gray-600 hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              style={{ '--tw-ring-color': colors.navy }}
              aria-haspopup="menu"
              aria-expanded={isBellMenuOpen}
              aria-label={hasUpdate ? 'Notifications — new app update available' : 'Notifications'}
              title={hasUpdate ? 'New app update available' : 'Notifications'}
            >
              <Bell className="w-5 h-5" />
              {hasUpdate && (
                <span
                  className="absolute top-2 right-2 block h-2.5 w-2.5 rounded-full bg-red-500 ring-2 ring-white"
                  aria-hidden="true"
                />
              )}
            </button>

            {isBellMenuOpen && (
              <div
                role="menu"
                aria-label="Notifications"
                className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl border border-gray-100 py-1 z-50 overflow-hidden"
                style={{ borderRadius: radii.lg }}
              >
                {hasUpdate && appInfo ? (
                  <div className="px-4 py-3">
                    <p className="text-sm font-semibold text-gray-900">
                      🎉 New version available
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Version {appInfo.version}
                      {formatSize(appInfo.size) ? ` · ${formatSize(appInfo.size)}` : ''}
                    </p>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={handleDownloadClick}
                      disabled={isDownloading}
                      className="mt-3 flex items-center justify-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium text-white disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                      style={{ backgroundColor: colors.navy, borderRadius: radii.lg }}
                    >
                      {isDownloading ? (
                        <span className="w-4 h-4 border-2 border-white/60 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                      {isDownloading ? 'Starting…' : 'Download App'}
                    </button>
                  </div>
                ) : (
                  <div className="px-4 py-3 text-sm text-gray-500">
                    You're all caught up.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Navy circular avatar → profile / logout menu */}
          <div className="relative" ref={profileMenuRef}>
            <button
              type="button"
              ref={avatarBtnRef}
              onClick={() => setIsProfileMenuOpen((open) => !open)}
              className="flex items-center justify-center h-10 w-10 rounded-full text-white font-semibold transition-transform hover:scale-105 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              style={{
                backgroundColor: colors.navy,
                borderRadius: radii.full,
                // status-colored ring (2px) sitting just outside the avatar
                boxShadow: `0 0 0 2px ${colors.white}, 0 0 0 4px ${statusRingColor}`,
                '--tw-ring-color': colors.navy,
              }}
              aria-haspopup="menu"
              aria-expanded={isProfileMenuOpen}
              aria-label={`Account menu for ${displayName}`}
              title={`${displayName}${getStatusColor ? ` — ${getStatusColor()}` : ''}`}
            >
              <span className="text-sm leading-none select-none">{initials}</span>
            </button>

            {isProfileMenuOpen && (
              <div
                role="menu"
                aria-label="Account"
                ref={menuRef}
                onKeyDown={handleMenuKeyDown}
                className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-100 py-1 z-50 overflow-hidden"
                style={{ borderRadius: radii.lg }}
              >
                <div className="px-4 py-2 border-b border-gray-100">
                  <p className="text-sm font-semibold text-gray-900 truncate">{displayName}</p>
                  {currentUser?.role && (
                    <p className="text-xs text-gray-500 truncate capitalize">{currentUser.role}</p>
                  )}
                </div>

                <button
                  type="button"
                  role="menuitem"
                  onClick={handleReportsClick}
                  disabled={isGeneratingToken}
                  className="flex items-center gap-2 w-full px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed transition-colors focus:outline-none focus:bg-gray-100"
                >
                  {isGeneratingToken ? (
                    <span className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <FileBarChart className="w-4 h-4 text-blue-600" />
                  )}
                  {isGeneratingToken ? 'Connecting…' : 'Reports'}
                </button>

                {/* Download App — available to every logged-in user */}
                <button
                  type="button"
                  role="menuitem"
                  onClick={handleDownloadClick}
                  disabled={isDownloading}
                  className="flex items-center gap-2 w-full px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed transition-colors focus:outline-none focus:bg-gray-100"
                >
                  {isDownloading ? (
                    <span className="w-4 h-4 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Download className="w-4 h-4 text-navy" style={{ color: colors.navy }} />
                  )}
                  {isDownloading ? 'Starting…' : 'Download App'}
                  {hasUpdate && (
                    <span className="ml-auto text-[10px] font-semibold text-red-600 bg-red-50 px-1.5 py-0.5 rounded-full">
                      New
                    </span>
                  )}
                </button>

                {/* {currentUser && (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={handleSubscriptionsClick}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none focus:bg-gray-100"
                  >
                    <ShieldCheck className="w-4 h-4 text-emerald-600" />
                    Subscriptions
                  </button>
                )} */}

                {handleLogout && (
                  <button
                    type="button"
                    role="menuitem"
                    onClick={handleLogoutClick}
                    className="flex items-center gap-2 w-full px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors focus:outline-none focus:bg-red-50"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Divider + "← Dashboard" button — hidden only on the bare dashboard list */}
          {showDashboardButton && (
            <>
              {/* 1px vertical divider between avatar and Dashboard button */}
              <span
                className="w-px h-8 bg-gray-200"
                style={{ backgroundColor: colors.gray200 }}
                aria-hidden="true"
              />

              {/* Outlined Dashboard button — arrow + label; icon-only below `lg` (T7.3), where title + aria-label carry the name the hidden text no longer provides. */}
              <button
                type="button"
                onClick={handleBackToDashboard}
                className="flex items-center gap-2 h-10 px-3 lg:px-4 rounded-lg border text-sm font-medium text-gray-700 border-gray-300 bg-white transition-colors hover:bg-gray-100 active:bg-gray-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
                style={{ borderRadius: radii.lg, '--tw-ring-color': colors.navy }}
                aria-label="Dashboard"
                title="Dashboard"
              >
                <ArrowLeft className="w-4 h-4 flex-shrink-0" />
                <span className="hidden lg:inline">Dashboard</span>
              </button>
            </>
          )}
        </div>
      </div>

      <SubscriptionStatusModal
        isOpen={isSubscriptionModalOpen}
        onClose={() => setIsSubscriptionModalOpen(false)}
        currentUser={currentUser}
      />
    </header>
  );
};

export default AppHeader;
